import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AppConfigSettings,
  AuthUserStore,
  UserState,
} from '@arc/arc-shared-frontend';
import { Store } from '@ngrx/store';
import { distinct, map, Observable, switchMap, take } from 'rxjs';

interface IsEnabled {
  enabled: boolean;
}

/* istanbul ignore next */
@Injectable({
  providedIn: 'root',
})
export class TenantHttpClientService {
  private _http: HttpClient = inject(HttpClient);
  private _store: Store = inject(Store);
  private appConfig = inject(AppConfigSettings);
  private baseUrl = this.appConfig.getconfig('tenantServiceUrl');

  public getFeatureIsEnabled(
    applicationId: string,
    featureId: string,
  ): Observable<boolean> {
    return this.executeServiceWithUserData<boolean>((user) => {
      const { activeTenant } = user;
      // `tenantUsername` is a real field on the API's tenant payload, not declared on
      // @arc/arc-shared-frontend's `UserTenant` type (only `tenantName` is). Bracket
      // notation is intentional here — do NOT let `eslint --fix` rewrite this to
      // `activeTenant.tenantUsername` (that field doesn't exist on the type, would fail
      // to compile) or to `activeTenant.tenantName` (compiles, but is a different,
      // wrong value — this URL has relied on `tenantUsername` in production since it
      // was written).
      const url =
        this.baseUrl +
        // eslint-disable-next-line @typescript-eslint/dot-notation
        `/v1/tenants/by-username/${activeTenant['tenantUsername']}/applications/${applicationId}/features/${featureId}/is-enabled`;
      const request = this._http
        .get<IsEnabled>(url)
        .pipe(map((res) => res.enabled));
      return request;
    });
  }

  private executeServiceWithUserData<T>(
    executeClientCall: (user: UserState) => Observable<T>,
  ): Observable<T> {
    return this._store.select(AuthUserStore).pipe(
      distinct((user) => user.activeTenant.tenantId),
      take(1),
      switchMap((user) => executeClientCall(user)),
    );
  }
}
